import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Promisable } from "type-fest";

import {
  approverPartyForAssessor,
  DecideAssessmentRequestSchema,
  displayName,
  documentOf,
  StartFormDraftRequestSchema,
  type Assessment,
  type CurrentUser,
  type DecideAssessmentRequest,
  type PersonInvolvement,
  type SubmissionNumber,
} from "@thalamos/haven-common";

import api, { apiHooks } from "../../api/index.js";
import {
  AssessmentFacts,
  type AssessmentFact,
} from "../../components/AssessmentFacts/AssessmentFacts.js";
import { hiddenIn } from "../../components/AssessmentForm/answering.js";
import { ChoiceOptionLabel } from "../../components/AssessmentForm/ChoiceOptionLabel.js";
import {
  DecisionPanel,
  type DecisionOutcome,
  type ReturnRequest,
} from "../../components/DecisionPanel/DecisionPanel.js";
import {
  ReturnedForChangesDialog,
  type ReopenOutcome,
} from "../../components/ReturnedForChangesDialog/ReturnedForChangesDialog.js";
import { useUser } from "../../Context/UserContext.js";
import { createRoute } from "../../routing/createRoute.js";
import {
  assessmentDecision,
  assessorNameOf,
  deciderNameOf,
} from "../Persons/involvementManagement.js";
import {
  useAssessment,
  useInstrumentForm,
  type FormLoading,
} from "./AssessmentContext.js";
import { latestSubmission } from "./latestSubmission.js";
import { reviewOf, type ReviewedAnswer } from "./submissionReview.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/**
 * The whole of a submitted assessment, read only: where an Approver reviews
 * and decides it, where a finished one is read, and what a returned one's
 * owner opens on, under why it came back, until they reopen it.
 */
export const AssessmentReview = () => {
  const { assessment } = useAssessment();

  return assessment === undefined ? null : (
    <AssessmentReviewFor assessment={assessment} />
  );
};

const AssessmentReviewFor = ({ assessment }: { assessment: Assessment }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { reread } = useAssessment();
  const { loading, retry } = useInstrumentForm(assessment);
  const [{ data: person, response: personResponse }] = apiHooks.person.byId(
    assessment.personId,
  );
  const [{ data: involvements, response }] = apiHooks.involvement.forPerson(
    assessment.personId,
  );

  // A 4xx body resolves onto `data`, so each is only taken from a 200.
  const known = response?.status === 200 ? involvements : undefined;
  const read = personResponse?.status === 200 ? person : undefined;
  const reopenable =
    assessment.status === "returned" && user?.id === assessment.ownerUserId;

  const reopen = async (
    submissionNumber: SubmissionNumber,
  ): Promise<ReopenOutcome> => {
    const started = await api.formDraft
      .create(
        assessment.id,
        StartFormDraftRequestSchema.parse({ fromSubmission: submissionNumber }),
      )
      .catch(() => undefined);

    // Reading it again open is what has `AssessmentHome` show the overview, on
    // the draft just started.
    if (started?.status === 201) {
      try {
        await reread();
      } catch {
        // The change notification may still bring it.
      }

      return "reopened";
    }

    return started?.status === 409 ? "changed" : "failed";
  };

  const decide = async (
    submissionNumber: SubmissionNumber,
    request: DecideAssessmentRequest,
  ): Promise<DecisionOutcome> => {
    const decided = await api.assessment.decide(
      assessment.id,
      submissionNumber,
      request,
    );

    if (decided.status === 201) {
      navigate(createRoute.person(assessment.personId));
      return "decided";
    }

    // A 404 is an assessment this account can no longer read — deleted
    // since, say — so trying again cannot help.
    return decided.status === 409 || decided.status === 404
      ? "changed"
      : "failed";
  };

  return (
    <AssessmentReviewContent
      assessment={assessment}
      loading={loading}
      involvements={known}
      viewer={user}
      personName={read === undefined ? undefined : displayName(read.names)}
      mayDecide={assessmentDecision(user, known, read)(assessment)}
      onRetry={retry}
      onApprove={(submissionNumber) =>
        decide(
          submissionNumber,
          DecideAssessmentRequestSchema.parse({ outcome: "approved" }),
        )
      }
      onReturn={decide}
      onReopen={reopenable ? reopen : undefined}
    />
  );
};

const Answer = ({ answer }: { answer: ReviewedAnswer }) =>
  answer.kind === "text" ? (
    answer.text
  ) : (
    // The title alone: an option's description is for choosing it.
    <ChoiceOptionLabel
      option={{ value: answer.option.value, title: answer.option.title }}
    />
  );

/**
 * The review's rendering, with everything it shows injected so a story or a
 * test can show any state of it without an API client.
 *
 * Where the form cannot be loaded, the answers are still shown, by their keys
 * and bare values, since the submission holds them all. The decision is
 * offered only where `mayDecide` says so, and is absent rather than disabled
 * otherwise.
 */
export const AssessmentReviewContent = ({
  assessment,
  loading,
  involvements,
  viewer,
  personName,
  mayDecide,
  onRetry,
  onApprove,
  onReturn,
  onReopen,
}: {
  assessment: Assessment;
  loading: FormLoading;
  /** Who is involved with the young person; undefined until read. */
  involvements: PersonInvolvement[] | undefined;
  viewer: CurrentUser | undefined;
  /** The young person's name, which approving is worded around; undefined until read. */
  personName: string | undefined;
  /** Whether this session may decide it; see `assessmentDecision`. */
  mayDecide: boolean;
  /** Tries loading the form again, where it could not be. */
  onRetry: () => void;
  /** Approves the submission shown, named by its number. */
  onApprove: (
    submissionNumber: SubmissionNumber,
  ) => Promisable<DecisionOutcome>;
  /** Returns the submission shown, named by its number. */
  onReturn: (
    submissionNumber: SubmissionNumber,
    request: ReturnRequest,
  ) => Promisable<DecisionOutcome>;
  /**
   * Reopens the assessment from the returned submission shown, named by its
   * number; absent where it is not this viewer's to reopen. Given, why it was
   * returned is put to them over the answers until they reopen it.
   */
  onReopen?:
    | ((submissionNumber: SubmissionNumber) => Promisable<ReopenOutcome>)
    | undefined;
}) => {
  const { t } = useTranslation();
  // Undefined until the reader expands or collapses a domain; until then only
  // the first is open. It cannot seed the state, because the form loads after
  // the first render.
  const [chosen, setChosen] = useState<ReadonlySet<string>>();

  const submission = latestSubmission(assessment);
  const returned =
    submission?.decision?.outcome === "returned"
      ? submission.decision
      : undefined;
  const whyReturned =
    onReopen === undefined ||
    submission === undefined ||
    returned === undefined ? null : (
      <ReturnedForChangesDialog
        reason={returned.reason}
        approverName={deciderNameOf(involvements, returned.decidedByUserId)}
        returnedAt={returned.decidedAt}
        onReopen={() => onReopen(submission.number)}
      />
    );

  if (loading.kind === "loading") {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
          {t("common.loading")}
        </Typography>
        {whyReturned}
      </>
    );
  }

  if (submission === undefined) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        {t("assessment.review.notSubmitted")}
      </Alert>
    );
  }

  const assessor =
    assessorNameOf(assessment, viewer, involvements) ??
    t("assessment.overview.assessorUnknown");
  const { decision } = submission;
  const approver =
    decision === null
      ? approverPartyForAssessor(involvements ?? [], assessment.ownerUserId)
          ?.name
      : deciderNameOf(involvements, decision.decidedByUserId);
  // "None yet" says the involvements were read and name no Approver; unread,
  // or once decided by someone they no longer name, the Approver is unknown.
  const noApproverNamed =
    decision === null && involvements !== undefined
      ? "assessment.overview.noApprover"
      : "assessment.review.approverUnknown";
  const facts: AssessmentFact[] = [
    { label: t("assessment.overview.assessor"), value: assessor },
    ...(assessment.definitive
      ? [
          {
            label: t("assessment.overview.approver"),
            value: approver ?? t(noApproverNamed),
          },
        ]
      : []),
    {
      label: t("assessment.overview.started"),
      value: CALENDAR_DATE.format(assessment.startedAt),
    },
    {
      label: t("assessment.review.submitted"),
      value: CALENDAR_DATE.format(submission.submittedAt),
    },
    ...(decision?.outcome === "approved"
      ? [
          {
            label: t("assessment.review.approved"),
            value: CALENDAR_DATE.format(decision.decidedAt),
          },
        ]
      : []),
  ];

  const { domains, itemCount } = reviewOf(
    loading.kind === "ready"
      ? {
          form: loading.loaded.form,
          groupsNotAsked: hiddenIn(
            loading.loaded.schema,
            loading.loaded.form,
            documentOf(submission.responses),
            { now: submission.submittedAt.toISOString() },
          ).groups,
          submission,
        }
      : { form: [], groupsNotAsked: new Set(), submission },
  );
  const expanded =
    chosen ?? new Set(domains[0] === undefined ? [] : [domains[0].group]);
  const allExpanded = domains.every(({ group }) => expanded.has(group));

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
        {loading.kind === "failed" ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                {t("assessment.review.retry")}
              </Button>
            }
          >
            {t("assessment.review.formUnavailable")}
          </Alert>
        ) : null}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={2}
        >
          <Stack spacing={0.5}>
            <Typography variant="h5" component="h2">
              {t("assessment.review.domains", { count: domains.length })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("assessment.review.summary", {
                assessor,
                date: CALENDAR_DATE.format(submission.submittedAt),
                count: itemCount,
              })}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              disabled={allExpanded}
              onClick={() => {
                setChosen(new Set(domains.map(({ group }) => group)));
              }}
            >
              {t("assessment.review.expandAll")}
            </Button>
            <Button
              size="small"
              disabled={expanded.size === 0}
              onClick={() => {
                setChosen(new Set());
              }}
            >
              {t("assessment.review.collapseAll")}
            </Button>
          </Stack>
        </Stack>

        <div>
          {domains.map(({ group, title, items }) => (
            <Accordion
              key={group}
              variant="outlined"
              disableGutters
              expanded={expanded.has(group)}
              onChange={(_event, isExpanded) => {
                setChosen(() => {
                  const next = new Set(expanded);
                  if (isExpanded) {
                    next.add(group);
                  } else {
                    next.delete(group);
                  }
                  return next;
                });
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                // MUI labels the details region from these two ids.
                id={`review-domain-${group}`}
                aria-controls={`review-domain-${group}-items`}
              >
                <Typography component="span" sx={{ fontWeight: "bold" }}>
                  {t("assessment.review.domain", {
                    domain: title,
                    count: items.length,
                  })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {items.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 2, pb: 2 }}
                  >
                    {t("assessment.review.noItems")}
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("assessment.review.item")}</TableCell>
                        <TableCell>{t("assessment.review.rating")}</TableCell>
                        <TableCell>{t("assessment.review.comment")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map(
                        ({ item, title: itemTitle, answer, comment }) => (
                          <TableRow key={item}>
                            <TableCell component="th" scope="row">
                              {itemTitle}
                            </TableCell>
                            <TableCell>
                              <Answer answer={answer} />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                              {comment}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </Stack>

      {/* A set width on wider screens: sized by its content, the decision's
          explanation would take the row from the answers. */}
      <Stack
        spacing={3}
        sx={{
          alignSelf: "flex-start",
          width: { xs: "100%", md: 360 },
          flexShrink: 0,
        }}
      >
        {/* Waits for the young person's name, which the approval's
            confirmation is worded around. */}
        {mayDecide && personName !== undefined ? (
          <DecisionPanel
            assessorName={assessor}
            personName={personName}
            onApprove={() => onApprove(submission.number)}
            onReturn={(request) => onReturn(submission.number, request)}
          />
        ) : null}
        <AssessmentFacts facts={facts} />
      </Stack>
      {whyReturned}
    </Stack>
  );
};
